<script lang="ts">
  import { onMount } from "svelte";
  import OrderDetailPage from "@retailer/svelte/OrderDetail/Page.svelte";
  import Spinner from "@shared/svelte/Components/SpinnerInternal.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { params, goto } from "@roxi/routify";
  import retailerContext from "@modern/services/retailer_context";
  import * as _ from "lodash";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import Flash from "@shared/services/flashv2.js";
  import SalesRep from "@modern/services/angularjs_shims/sales_rep";
  import OrderEvent from "@shared/services/order_event";
  import Order from "@shared/services/order";
  import { pluralContainerType } from "@shared/services/product";
  import type { RetailerType } from "@shared/types/Retailer";
  import { shouldUseRetailerFulfillmentMethodForDistributor } from "@retailer/services/fulfillment_methods_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  const orderRes = async () => {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: `/api/retailer/orders/${$params.orderId}`,
      });
      if (!res) {
        Flash.queue({ message: "No such order found! Please try to log in again.", type: "error" });
        return $goto("/orders");
      }
      return res;
    } catch (e) {
      return [];
    }
  };

  const stateSettingsRes = async () => {
    let res = await HttpPromise({
      method: "GET",
      url: "/api/retailer/state_settings",
    });
    StateSettingsStore.set(res);
    return res;
  };

  const distributorsRes = async () => {
    let res = [];
    res = await HttpPromise({
      method: "GET",
      url: "/api/retailer/distributors",
      data: {
        with_cutoff_time: true,
        with_integrated: true,
        with_sales_reps: true,
        with_domains: true,
      },
    }).catch(() => (res = []));
    DistributorsStore.set(res);
    return res;
  };

  const orderEmailTemplateRes = async () => {
    let res = await HttpPromise({
      method: "GET",
      url: `/api/retailer/orders/${$params.orderId}/email_template`,
    });
    return res;
  };

  const parseOrderEmailTemplateRes = res => {
    return res.mailto_string;
  };

  const retailer = retailerContext.get() as RetailerType;
  const showOrderEmailTemplate = $params.showEmailTemplate === "true";

  const load = async () => {
    const promises = [distributorsRes(), orderRes(), stateSettingsRes()];

    if (showOrderEmailTemplate) {
      promises.push(orderEmailTemplateRes());
    }

    let results = await Promise.all(promises);
    const order = results[1];
    const orderEmailTemplate = showOrderEmailTemplate
      ? parseOrderEmailTemplateRes(results[3])
      : null;

    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        order?.distributor?.id,
        order?.distributor?.uses_warehouse_pickup,
        stagedRolloutCheck("marketplace__fulfillment_methods") &&
          order?.distributor?.uses_wine_cellar,
      )
    ) {
      await RetailerFulfillmentMethodsStore.updateDistributorFulfillmentMethod(
        order?.distributor?.id,
      );
    }

    return {
      order,
      orderEmailTemplate,
    };
  };

  const orderEventService = OrderEvent(window["C"]);
  const productService = {
    pluralContainerType,
  };
  const services = {
    SalesRep,
    OrderEvent: orderEventService,
    Order,
    Product: productService,
  };

  if (showOrderEmailTemplate) {
    onMount(async () => {
      const data = await load();

      if (data.orderEmailTemplate) {
        window.location.href = data.orderEmailTemplate;
      }
    });
  }
</script>

{#await load()}
  <Spinner />
{:then data}
  <OrderDetailPage {retailer} order={data.order} {services} />
{/await}
